import { Timestamp } from "firebase/firestore";

/**
 * Signals shown as columns on the admin users CRM.
 * Counts are records the user actually saved, summed across all pets.
 * Order matches the pet directory menu.
 */
export const USER_ACTIVITY_COLUMNS = [
  { key: "events", label: "Events", title: "Events", collection: "events", kind: "any" },
  { key: "documents", label: "Docs", title: "Documents", collection: "petDocuments", kind: "document" },
  { key: "weight", label: "Weight", title: "Weight", collection: "weight", kind: "measure" },
  { key: "measures", label: "Measures", title: "Measures", collection: "petMeasures", kind: "measure" },
  { key: "clinics", label: "Clinics", title: "Clinics", collection: "clinics", kind: "any", dedupe: "directoryId" },
  { key: "labAnalyses", label: "Labs", title: "Lab tests", collection: "labAnalyses", kind: "any" },
  { key: "diagnoses", label: "Diagnoses", title: "Diagnoses", collection: "diagnoses", kind: "any" },
  { key: "medicines", label: "Meds", title: "Medicines", collection: "medicines", kind: "any" },
  { key: "nutrition", label: "Food", title: "Nutrition", collection: "nutritionItems", kind: "any" },
  {
    key: "petContacts",
    label: "Specialists",
    title: "Specialists",
    collection: "specialistContacts",
    kind: "any",
    dedupe: "directoryId",
    userCollection: "specialistDirectory",
  },
  { key: "ownerContacts", label: "Owners", title: "Owner contacts", collection: "ownerContacts", kind: "any" },
  { key: "journal", label: "Diary", title: "Diary", collection: "journalEntries", kind: "any" },
  {
    key: "exports",
    label: "Export",
    title: "Medical card and lab exports",
    collection: "medicalCardExports",
    kind: "any",
  },
];

export function emptyActivity() {
  const activity = {};
  USER_ACTIVITY_COLUMNS.forEach((col) => {
    activity[col.key] = 0;
  });
  return activity;
}

export function getTimestampValue(ts) {
  if (!ts) return null;
  if (ts instanceof Timestamp) return ts.toDate();
  if (ts?.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "string") {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function countsAsActivity(data, kind) {
  if (!data || typeof data !== "object") return false;

  if (kind === "document") {
    const attachments = Array.isArray(data.attachments) ? data.attachments.length : 0;
    const filled = attachments > 0 || hasText(data.number) || hasText(data.note);
    if (data.kind === "custom") {
      return filled || hasText(data.customTypeName) || hasText(data.title);
    }
    return filled;
  }

  if (kind === "measure") {
    const value = typeof data.value === "number" ? data.value : Number(data.value);
    return Number.isFinite(value) && value > 0;
  }

  return true;
}

export function summarizeActivityDocs(docs, column) {
  let count = 0;
  let latest = null;
  const keys = new Set();
  const kind = column?.kind || "any";

  docs.forEach((snap) => {
    const data = typeof snap?.data === "function" ? snap.data() : snap;
    if (!countsAsActivity(data, kind)) return;

    const created = getTimestampValue(data?.createdAt);
    const updated = getTimestampValue(data?.updatedAt);
    if (created && (!latest || created > latest)) latest = created;
    if (updated && (!latest || updated > latest)) latest = updated;

    if (column?.dedupe) {
      const raw = data?.[column.dedupe];
      const key =
        typeof raw === "string" && raw.trim()
          ? raw.trim()
          : snap.id || `row-${keys.size}`;
      keys.add(key);
      return;
    }

    count += 1;
  });

  return {
    count: column?.dedupe ? keys.size : count,
    latest,
    keys: column?.dedupe ? Array.from(keys) : null,
  };
}

export function sumPetActivity(pets) {
  const totals = emptyActivity();
  const keySets = {};
  USER_ACTIVITY_COLUMNS.forEach((col) => {
    if (col.dedupe) keySets[col.key] = new Set();
  });

  (pets || []).forEach((pet) => {
    USER_ACTIVITY_COLUMNS.forEach((col) => {
      if (col.dedupe) {
        (pet.activityKeys?.[col.key] || []).forEach((key) => keySets[col.key].add(key));
        return;
      }
      totals[col.key] += pet.activity?.[col.key] || 0;
    });
  });

  USER_ACTIVITY_COLUMNS.forEach((col) => {
    if (col.dedupe) totals[col.key] = keySets[col.key].size;
  });
  return totals;
}

/** Column whose saved records have the newest created/updated time. */
export function latestActivityColumn(pets) {
  let bestKey = null;
  let bestTime = 0;

  (pets || []).forEach((pet) => {
    const byKey = pet.activityLatestByKey || {};
    USER_ACTIVITY_COLUMNS.forEach((col) => {
      const date = byKey[col.key];
      if (!date) return;
      const time = date.getTime();
      if (time > bestTime) {
        bestTime = time;
        bestKey = col.key;
      }
    });
  });

  if (!bestKey) return null;
  return { key: bestKey, at: new Date(bestTime) };
}

export function calculateLastActivity(user) {
  const dates = [];

  const userCreated = getTimestampValue(user.createdAt);
  const userUpdated = getTimestampValue(user.updatedAt);
  if (userCreated) dates.push(userCreated);
  if (userUpdated) dates.push(userUpdated);

  (user.pets || []).forEach((pet) => {
    const petCreated = getTimestampValue(pet.createdAt);
    const petUpdated = getTimestampValue(pet.updatedAt);
    if (petCreated) dates.push(petCreated);
    if (petUpdated) dates.push(petUpdated);
    if (pet.activityLatest) dates.push(pet.activityLatest);
  });

  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
}
